import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  type QueryConstraint,
  type DocumentData,
  serverTimestamp,
  onSnapshot,
} from 'firebase/firestore'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { db } from '@/lib/firebase'

export function useCollection<T extends DocumentData>(
  collectionName: string,
  constraints: QueryConstraint[] = [],
  enabled = true
) {
  return useQuery({
    queryKey: [collectionName, ...constraints.map(String)],
    queryFn: async () => {
      const q = query(collection(db, collectionName), ...constraints)
      const snapshot = await getDocs(q)
      return snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as T & { id: string }))
    },
    enabled,
  })
}

export function useDocument<T extends DocumentData>(
  collectionName: string,
  documentId: string | undefined,
  enabled = true
) {
  return useQuery({
    queryKey: [collectionName, documentId],
    queryFn: async () => {
      if (!documentId) return null
      const docRef = doc(db, collectionName, documentId)
      const snapshot = await getDoc(docRef)
      if (!snapshot.exists()) return null
      return { id: snapshot.id, ...snapshot.data() } as T & { id: string }
    },
    enabled: enabled && !!documentId,
  })
}

export function useAddDocument(collectionName: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: DocumentData) => {
      const docRef = await addDoc(collection(db, collectionName), {
        ...data,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
      })
      return docRef.id
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [collectionName] })
    },
  })
}

/**
 * @param updatedAtField  Collections the apps also read (e.g. `businesses`) use the
 *   camelCase `updatedAt`; admin-only collections keep their snake_case `updated_at`.
 */
export function useUpdateDocument(collectionName: string, updatedAtField = 'updated_at') {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: DocumentData }) => {
      const docRef = doc(db, collectionName, id)
      await updateDoc(docRef, { ...data, [updatedAtField]: serverTimestamp() })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [collectionName] })
    },
  })
}

export function useDeleteDocument(collectionName: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const docRef = doc(db, collectionName, id)
      await deleteDoc(docRef)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [collectionName] })
    },
  })
}

export { collection, query, where, orderBy, limit, onSnapshot, doc, getDoc, getDocs }
